import { createCloudRunAxios } from './axios';

const useRemiApiRollout = import.meta.env.VITE_APP_ENV === 'uat';
const rolloutApi = createCloudRunAxios(useRemiApiRollout ? 'remi-api' : 'remi-sync');

// Types
export interface RolloutTokenResponse {
  success: boolean;
  token: string;
  expires_in: number;
  organization_id: string;
}

export interface RolloutCredential {
  credential_id: string;
  app_key: string;
  connected_at: string;
  status?: string;
  [key: string]: unknown;
}

export interface RolloutCredentialsResponse {
  success?: boolean;
  status?: string;
  organization_id?: string;
  credentials: RolloutCredential[];
  count?: number;
  connections?: Record<string, unknown>[];
  connections_count?: number;
}

export interface RolloutCredentialConnectedRequest {
  credential_id: string;
  app: string;
  organization_id: string;
  metadata?: Record<string, unknown>;
}

export interface RolloutCredentialConnectedResponse {
  status: string;
  message: string | null;
  enqueued?: number | null;
}

export interface RolloutCredentialDeletedRequest {
  credential_id: string;
  organization_id: string;
}

export interface RolloutCredentialDeletedResponse {
  status: string;
  message: string | null;
  enqueued?: number | null;
}

export interface RolloutTestConnectionResponse {
  status: string;
  connected: boolean;
  provider?: string | null;
  reason?: string | null;
}

export interface RolloutStage {
  id: number | string;
  /** Spec field name. The currently-deployed backend returns `name` instead — read both via `getStageLabel`. */
  title?: string;
  name?: string;
  enabled: boolean;
  [key: string]: unknown;
}

/** Read the user-facing label from a stage, tolerating either field. */
export const getStageLabel = (stage: RolloutStage): string =>
  stage.title ?? stage.name ?? '';

export interface RolloutStagesResponse {
  status: string;
  connection_id: string;
  stages: RolloutStage[];
  enabled_stages?: RolloutStage[];
  count?: number;
}

export interface RolloutSaveStagesResponse {
  status: string;
  connection_id: string;
  stages?: RolloutStage[];
  enabled_stages?: RolloutStage[];
  saved_count: number;
  count?: number;
}

type RolloutStagesApiResponse = Partial<RolloutStagesResponse> & {
  success?: boolean;
  organization_id?: string;
  credential_id?: string;
};

type RolloutSaveStagesApiResponse = Partial<RolloutSaveStagesResponse> & {
  success?: boolean;
  organization_id?: string;
  credential_id?: string;
};

const normalizeStages = (stages: RolloutStage[] | undefined): RolloutStage[] =>
  (stages || []).map((stage) => ({
    ...stage,
    title: stage.title ?? stage.name ?? '',
    name: stage.name ?? stage.title ?? '',
    enabled: stage.enabled ?? true,
  }));

const normalizeStagesResponse = (
  data: RolloutStagesApiResponse,
  fallbackId: string
): RolloutStagesResponse => {
  const stages = normalizeStages(data.stages ?? data.enabled_stages);
  return {
    status: data.status ?? (data.success ? 'ok' : 'error'),
    connection_id: data.connection_id ?? data.credential_id ?? fallbackId,
    stages,
    enabled_stages: normalizeStages(data.enabled_stages),
    count: data.count ?? stages.length,
  };
};

/**
 * Get JWT token required by Rollout's CredentialsManager component
 */
export const getRolloutToken = async (orgId: string): Promise<RolloutTokenResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${orgId}/rollout/token`
      : `/api/v1/connection/token/${orgId}`;
    const response = await rolloutApi.get<RolloutTokenResponse>(path);
    return response.data;
  } catch (error) {
    console.error('Error getting Rollout token:', error);
    throw error;
  }
};

/**
 * Get credentials (connection status) for an organization
 */
export const getRolloutCredentials = async (orgId: string): Promise<RolloutCredentialsResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${orgId}/rollout/credentials`
      : `/api/v1/connection/credentials/${orgId}`;
    const response = await rolloutApi.get<RolloutCredentialsResponse>(path);
    return response.data;
  } catch (error) {
    console.error('Error getting Rollout credentials:', error);
    throw error;
  }
};

/**
 * Notify backend when a CRM credential is connected
 */
export const notifyCredentialConnected = async (
  data: RolloutCredentialConnectedRequest
): Promise<RolloutCredentialConnectedResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${data.organization_id}/rollout/credential-connected`
      : '/webhook/crm/credential-connected';
    const response = await rolloutApi.post<RolloutCredentialConnectedResponse>(
      path,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error notifying credential connected:', error);
    throw error;
  }
};

/**
 * Notify backend when a CRM credential is deleted/disconnected
 */
export const notifyCredentialDeleted = async (
  data: RolloutCredentialDeletedRequest
): Promise<RolloutCredentialDeletedResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${data.organization_id}/rollout/credential-deleted`
      : '/webhook/crm/credential-deleted';
    const response = await rolloutApi.post<RolloutCredentialDeletedResponse>(
      path,
      data
    );
    return response.data;
  } catch (error) {
    console.error('Error notifying credential deleted:', error);
    throw error;
  }
};

/**
 * Test CRM connection for an organization
 */
export const testRolloutConnection = async (
  orgId: string
): Promise<RolloutTestConnectionResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${orgId}/rollout/test-connection`
      : `/api/v1/connection/test/${orgId}`;
    const response = await rolloutApi.get<RolloutTestConnectionResponse>(path);
    return response.data;
  } catch (error) {
    console.error('Error testing Rollout connection:', error);
    throw error;
  }
};

/**
 * Get stages for a connection. Accepts a connection UUID or a legacy org ID
 * (backend resolves the latter to the org's most-recent active connection).
 */
export const getRolloutStages = async (
  connectionOrOrgId: string
): Promise<RolloutStagesResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${connectionOrOrgId}/rollout/stages`
      : `/api/v1/stages/${connectionOrOrgId}`;
    const response = await rolloutApi.get<RolloutStagesApiResponse>(path);
    return normalizeStagesResponse(response.data, connectionOrOrgId);
  } catch (error) {
    console.error('Error getting Rollout stages:', error);
    throw error;
  }
};

/**
 * Save the full enabled stages allowlist. POST replaces — send everything that
 * should be enabled, not just the delta.
 */
export const saveRolloutStages = async (
  connectionOrOrgId: string,
  enabledStages: Array<{ id: number | string; title?: string; name?: string }>
): Promise<RolloutSaveStagesResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/organizations/${connectionOrOrgId}/rollout/stages`
      : `/api/v1/stages/${connectionOrOrgId}`;
    const response = await rolloutApi.post<RolloutSaveStagesApiResponse>(
      path,
      {
        enabled_stages: enabledStages.map((stage) => {
          const label = stage.name ?? stage.title ?? '';
          return { id: stage.id, name: label, title: label };
        }),
      }
    );
    return {
      status: response.data.status ?? (response.data.success ? 'ok' : 'error'),
      connection_id: response.data.connection_id ?? response.data.credential_id ?? connectionOrOrgId,
      stages: normalizeStages(response.data.stages ?? response.data.enabled_stages),
      enabled_stages: normalizeStages(response.data.enabled_stages),
      saved_count: response.data.saved_count ?? response.data.count ?? enabledStages.length,
      count: response.data.count,
    };
  } catch (error) {
    console.error('Error saving Rollout stages:', error);
    throw error;
  }
};

export interface RolloutMetadataResponse {
  status: string;
  connection_id: string;
  metadata: Record<string, unknown>;
}

/** Fetch the connection's metadata blob (credentials are server-side redacted). */
export const getConnectionMetadata = async (
  connectionOrOrgId: string
): Promise<RolloutMetadataResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/rollout/connection/${connectionOrOrgId}/metadata`
      : `/api/v1/connection/${connectionOrOrgId}/metadata`;
    const response = await rolloutApi.get<RolloutMetadataResponse>(path);
    return response.data;
  } catch (error) {
    console.error('Error getting connection metadata:', error);
    throw error;
  }
};

/**
 * Shallow-merge keys into the connection's metadata. Requires org_admin role.
 * Backend returns the full post-merge blob — don't refetch.
 */
export const patchConnectionMetadata = async (
  connectionOrOrgId: string,
  patch: Record<string, unknown>
): Promise<RolloutMetadataResponse> => {
  try {
    const path = useRemiApiRollout
      ? `/api/v1/rollout/connection/${connectionOrOrgId}/metadata`
      : `/api/v1/connection/${connectionOrOrgId}/metadata`;
    const response = await rolloutApi.patch<RolloutMetadataResponse>(
      path,
      patch
    );
    return response.data;
  } catch (error) {
    console.error('Error patching connection metadata:', error);
    throw error;
  }
};
